import React, { memo } from 'react'
import {
    checkEmptyValue, checkIBPZero,
    valueStringByType, levelStringByType, unitStringByType
} from '../../plugins/common'

const PatientNumeric = memo(({
    isLeft,
    type,
    alarmString,
    isVSM,
    // 데이터
    vsData,
    settingStatus,
    outOfRange,
    units,
    isDetecting,
    parameterColors,
    // 함수
    getAlarmStringFn,
    getNIBPDiff,
    getAdaptiveFontSize,
    // 스타일 (resolver 결과)
    styles,
    // 32 전용 (optional)
    isAloneWithNibpTop,
    patientCnt,
}) => {
    const backgroundColor = 'transparent'
    const defaultValue = ['Temp1', 'Temp2'].indexOf(type) !== -1 ? '--.-' : ''
    const borderStyle = {
        borderLeftWidth: isLeft ? 1 : 0,
        borderRightWidth: isLeft ? 1 : 0,
        borderColor: '#383838',
    }

    // ======================== NIBP / IBP ========================
    if (['NIBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4'].indexOf(type) !== -1) {
        if (isVSM) {
            const nibpAloneClass = isAloneWithNibpTop?.() ? 'text-right' : 'flex'
            return (
                <div className="flex flex-1 px-2 py-2" style={{ backgroundColor, color: parameterColors.numericParameterColors[type], fontFamily: 'Roboto', ...borderStyle }}>
                    <div className="flex flex-col h-full">
                        <div style={styles.type}>{type}</div>
                        <div className="mb-auto" style={styles.unit}>{checkEmptyValue(units[type])}</div>
                        {['NIBP'].indexOf(type) !== -1 && (
                            <div style={styles.alarmString}>{getNIBPDiff()}</div>
                        )}
                    </div>
                    <div className="flex flex-col mt-auto ml-auto">
                        <div className={`${nibpAloneClass} mb-2 ml-auto`}>
                            {outOfRange.indexOf(type) !== -1 ? (
                                <div className="mr-1" style={styles.nibpValue}>XXX</div>
                            ) : type === 'NIBP' ? (
                                <>
                                    <div className="mr-1" style={styles.nibpValue}>{`${checkIBPZero(vsData[`${type.toLowerCase()}SysValue`], '', '', true)}/${checkIBPZero(vsData[`${type.toLowerCase()}DiaValue`], '', '', true)}`}</div>
                                    <div style={styles.nibpSubValue}>{`(${checkIBPZero(vsData[`${type.toLowerCase()}MapValue`], '', '', true)})`}</div>
                                </>
                            ) : (
                                <>
                                    <div className="mr-1" style={styles.nibpValue}>{`${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}SysValue`], '', '', false) : '-'}/${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}DiaValue`], '', '', false) : '-'}`}</div>
                                    <div style={styles.nibpSubValue}>{`(${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}MapValue`], '', '', false) : '-'})`}</div>
                                </>
                            )}
                        </div>
                        <div className={`${nibpAloneClass} ml-auto`}>
                            <div style={{ marginRight: isAloneWithNibpTop?.() ? 0 : 5, ...styles.nibpAlarm }}>{getAlarmStringFn(`${type}SYS`)}</div>
                            <div style={{ marginRight: isAloneWithNibpTop?.() ? 0 : 5, ...styles.nibpAlarm }}>{getAlarmStringFn(`${type}DIA`)}</div>
                            <div style={styles.nibpAlarm}>{getAlarmStringFn(`${type}MEAN`)}</div>
                        </div>
                    </div>
                </div>
            )
        }
        // non-VSM NIBP/IBP
        return (
            <div className="flex items-center flex-1 px-2 py-2" style={{ backgroundColor, color: parameterColors.numericParameterColors[type], fontFamily: 'Roboto', maxWidth: styles.containerMaxWidth, ...borderStyle }}>
                <div>
                    <div style={styles.type}>{settingStatus[`${type.toLowerCase()}Label`] || type}</div>
                    <div style={styles.unit}>{checkEmptyValue(units[type])}</div>
                    {['NIBP'].indexOf(type) !== -1 && (
                        <div style={styles.alarmString}>{getNIBPDiff()}</div>
                    )}
                </div>
                <div className="flex flex-col ml-auto">
                    <div className="flex mb-2 ml-auto">
                        {outOfRange.indexOf(type) !== -1 ? (
                            <div className="mr-1" style={styles.nibpValue}>XXX</div>
                        ) : type === 'NIBP' ? (
                            <>
                                <div className="mr-1" style={styles.nibpValue}>{`${checkIBPZero(vsData[`${type.toLowerCase()}SysValue`], '', '', true)}/${checkIBPZero(vsData[`${type.toLowerCase()}DiaValue`], '', '', true)}`}</div>
                                <div style={styles.nibpSubValue}>{`(${checkIBPZero(vsData[`${type.toLowerCase()}MapValue`], '', '', true)})`}</div>
                            </>
                        ) : (
                            <>
                                <div className="mr-1" style={styles.nibpValue}>{`${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}SysValue`], '', '', false) : '-'}/${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}DiaValue`], '', '', false) : '-'}`}</div>
                                <div style={styles.nibpSubValue}>{`(${vsData[isDetecting[type]] ? checkIBPZero(vsData[`${type.toLowerCase()}MapValue`], '', '', false) : '-'})`}</div>
                            </>
                        )}
                    </div>
                    <div className="flex ml-auto">
                        <div className="mr-1" style={styles.nibpAlarm}>{getAlarmStringFn(`${type}SYS`)}</div>
                        <div className="mr-1" style={styles.nibpAlarm}>{getAlarmStringFn(`${type}DIA`)}</div>
                        <div className="mr-1" style={styles.nibpAlarm}>{getAlarmStringFn(`${type}MEAN`)}</div>
                    </div>
                </div>
            </div>
        )
    }

    // ======================== GAS ========================
    if (['GAS'].indexOf(type) !== -1) {
        const gasMinWidth = styles.gasRightMinWidth || (patientCnt ? { minWidth: 70, maxWidth: 70 } : { minWidth: 80, maxWidth: 80 })
        return (
            <div className="flex flex-1 items-center px-2 py-2" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], ...borderStyle }}>
                <div className="flex-1">
                    <div style={styles.gasType || styles.type}>GAS (Ex/In)</div>
                    <div style={styles.gasValue}>{`O2: ${vsData[isDetecting['GasO2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasO2Ex']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasO2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasO2In']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                    <div style={styles.gasValue}>{`N2O: ${vsData[isDetecting['GasN2O']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasN2OEx']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasN2O']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasN2OIn']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                </div>
                <div className="ml-auto text-right" style={gasMinWidth}>
                    <div style={styles.gasValue}>{`CO2: ${vsData[isDetecting['GasCO2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasCo2Ex']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasCO2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasCo2In']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                    <div style={styles.gasValue}>{`SEV: ${vsData[isDetecting['GasAA1']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasSevEx']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasAA1']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasSevIn']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                    <div style={styles.gasValue}>{`DES: ${vsData[isDetecting['GasAA2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasDesEx']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}/${vsData[isDetecting['GasAA2']] ? (outOfRange.indexOf(type) !== -1 ? 'XXX' : checkEmptyValue(vsData[valueStringByType['GasDesIn']]?.toFixed(units['GAS'] === 'mmHg' ? 0 : 1))) : '-'}`}</div>
                </div>
            </div>
        )
    }

    // ======================== BIS ========================
    if (['BIS'].indexOf(type) !== -1) {
        return (
            <div className="flex items-center flex-1 px-2 py-2" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], ...borderStyle }}>
                <div className="flex-1">
                    <div style={styles.bisType || styles.type}>BIS</div>
                    <div className="flex" style={styles.bisValue || styles.alarmString}>{`SQI: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.sqiValue) : '-'}`}</div>
                    <div style={styles.bisValue || styles.alarmString}>{`EMG: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.emgValue) : '-'}`}</div>
                </div>
                <div className="ml-auto text-right" style={{ minWidth: 70, maxWidth: 70 }}>
                    <div style={styles.bisValue || styles.alarmString}>{`SR: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.srValue) : '-'}`}</div>
                    <div style={styles.bisValue || styles.alarmString}>{`SEF: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.sefValue) : '-'}`}</div>
                    <div style={styles.bisValue || styles.alarmString}>{`TP: ${vsData[isDetecting[type]] ? checkEmptyValue(vsData.tpValue) : '-'}`}</div>
                </div>
            </div>
        )
    }

    // ======================== EWS ========================
    if (['EWS'].indexOf(type) !== -1) {
        return (
            <div className={`flex flex-1 ${isVSM ? '' : 'items-center'} px-2 py-2 text-[#FFFFFF]`} style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], ...borderStyle }}>
                <div className={isVSM ? 'flex flex-col' : ''}>
                    <div style={styles.type}>{type}</div>
                    <div style={styles.unit}>{checkEmptyValue(units[type])}</div>
                    <div className={isVSM ? 'mt-auto' : ''} style={styles.alarmString}>{checkEmptyValue(vsData[levelStringByType[type]], 'Lv: ')}</div>
                </div>
                <div className={`${isVSM ? 'mt-auto' : ''} ml-auto`} style={styles.value}>{checkEmptyValue(vsData[valueStringByType[type]] == '100' ? '-' : vsData[valueStringByType[type]])}</div>
            </div>
        )
    }

    // ======================== 일반 (HR, SpO2, RR, EtCO2, Temp 등) ========================
    if (isVSM) {
        const showValueInColumn = patientCnt != null ? patientCnt < 4 : true
        const showValueOutside = patientCnt != null ? patientCnt >= 4 : false
        return (
            <div className="flex flex-1 px-2 py-2 text-[#FFFFFF]" style={{ backgroundColor, fontFamily: 'Roboto', color: parameterColors.numericParameterColors[type], ...borderStyle }}>
                <div className="flex flex-col" style={{ flexShrink: 0 }}>
                    <div style={styles.type}>{type}</div>
                    {['EtCO2', 'InCO2', 'Temp1', 'Temp2'].indexOf(type) !== -1
                        ? <div style={styles.unit}>{checkEmptyValue(units[type])}</div>
                        : <div style={styles.unit}>{unitStringByType[type] ? checkEmptyValue(settingStatus[unitStringByType[type]]) : checkEmptyValue(units[type])}</div>
                    }
                    {showValueInColumn && (
                        <div className="flex-1 mt-auto ml-auto" style={styles.value}>
                            {vsData[isDetecting[type]] ? checkEmptyValue(vsData[valueStringByType[type]]) : '-'}
                        </div>
                    )}
                    <div className="mt-auto" style={{ ...styles.alarmString, whiteSpace: 'nowrap' }}>{checkEmptyValue(alarmString)}</div>
                </div>
                {showValueOutside && (
                    <div className="mt-auto ml-auto text-right" style={{ minWidth: 0, flexShrink: 1, paddingRight: 2, ...styles.value }}>
                        {vsData[isDetecting[type]]
                            ? (outOfRange.indexOf(type) !== -1 ? (['Temp1', 'Temp2'].indexOf(type) !== -1 ? 'XX.X' : 'XXX') : checkEmptyValue(vsData[valueStringByType[type]], '', '', defaultValue))
                            : (['Temp1', 'Temp2'].indexOf(type) !== -1 ? '--.-' : '-')}
                    </div>
                )}
            </div>
        )
    }

    // non-VSM 일반
    const renderValue = () => {
        const sourceType = ((type === 'RESP' || type === 'RR') && vsData?.rrSource && vsData.rrSource !== 'Impedance') ? 'awRR' : type
        const isRROn = (type === 'RESP' || type === 'RR') ? !!settingStatus?.rrOnOff : true
        if (!isRROn) return ['Temp1', 'Temp2'].includes(type) ? '--.-' : '-'
        return vsData[isDetecting[sourceType]]
            ? (outOfRange.indexOf(type) !== -1
                ? (['Temp1', 'Temp2'].indexOf(type) !== -1 ? 'XX.X' : 'XXX')
                : checkEmptyValue(vsData[valueStringByType[sourceType]], '', '', defaultValue))
            : (['Temp1', 'Temp2'].indexOf(type) !== -1 ? '--.-' : '-')
    }
    const displayValue = renderValue()
    const adaptiveFontSize = getAdaptiveFontSize
        ? getAdaptiveFontSize(displayValue, styles.value?.fontSize)
        : styles.value?.fontSize

    return (
        <div className="flex flex-1 items-center px-2 py-2 text-[#FFFFFF]" style={{ backgroundColor, fontFamily: 'Roboto', maxWidth: styles.containerMaxWidth, color: parameterColors.numericParameterColors[type], ...borderStyle }}>
            <div style={['IPI'].indexOf(type) === -1 ? {} : { display: 'flex', flexDirection: 'column', height: '100%' }}>
                {type === 'PR' ? (
                    <div style={styles.type}>
                        {['SpO2', 'IBP1', 'IBP2', 'IBP3', 'IBP4', 'NIBP'].includes(vsData.hrprSource) ? `PR (${vsData.hrprSource})` : 'PR'}
                    </div>
                ) : (
                    <div style={styles.type}>{type === 'RESP' ? 'RR' : type}</div>
                )}
                {['IPI'].indexOf(type) === -1 && (
                    ['EtCO2', 'InCO2', 'Temp1', 'Temp2'].indexOf(type) !== -1
                        ? <div style={styles.unit}>{checkEmptyValue(units[type])}</div>
                        : <div style={styles.unit}>{unitStringByType[type] ? checkEmptyValue(settingStatus[unitStringByType[type]]) : checkEmptyValue(units[type])}</div>
                )}
                <div style={{ marginTop: ['IPI'].indexOf(type) === -1 ? '' : 'auto', ...styles.alarmString }}>{checkEmptyValue(alarmString)}</div>
            </div>
            <div className="ml-auto" style={{ ...styles.value, fontSize: adaptiveFontSize }}>
                {displayValue}
            </div>
        </div>
    )
}, (prevProps, nextProps) => {
    return (
        prevProps.type === nextProps.type &&
        prevProps.isLeft === nextProps.isLeft &&
        prevProps.isVSM === nextProps.isVSM &&
        prevProps.alarmString === nextProps.alarmString &&
        prevProps.vsData === nextProps.vsData &&
        prevProps.settingStatus === nextProps.settingStatus &&
        prevProps.outOfRange === nextProps.outOfRange &&
        prevProps.units === nextProps.units &&
        prevProps.isDetecting === nextProps.isDetecting &&
        prevProps.parameterColors === nextProps.parameterColors &&
        prevProps.styles === nextProps.styles &&
        prevProps.patientCnt === nextProps.patientCnt
    )
})

PatientNumeric.displayName = 'PatientNumeric'

export default PatientNumeric